import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";

const STORAGE_KEY = "storefront_customer_session_v1";

function loadStoredSession() {
    try {
        const raw = localStorage.getItem(STORAGE_KEY);
        if (!raw) return { token: "", user: null };
        const parsed = JSON.parse(raw);
        return {
            token: typeof parsed?.token === "string" ? parsed.token : "",
            user: parsed?.user ?? null,
        };
    } catch {
        return { token: "", user: null };
    }
}

const AuthSessionContext = createContext(null);

export function AuthSessionProvider({ children }) {
    const [session, setSession] = useState(() => loadStoredSession());
    const [addressStructure, setAddressStructure] = useState({ countries: [] });
    const [addressStructureLoading, setAddressStructureLoading] = useState(false);
    const [addresses, setAddresses] = useState([]);
    const [addressesLoading, setAddressesLoading] = useState(false);

    useEffect(() => {
        try {
            localStorage.setItem(STORAGE_KEY, JSON.stringify(session));
        } catch {
            // no-op
        }

        if (session?.token) {
            localStorage.setItem("customer_token", session.token);
        } else {
            localStorage.removeItem("customer_token");
        }
    }, [session]);

    const refreshAddressStructure = useCallback(async () => {
        if (!session?.token) {
            setAddressStructure({ countries: [] });
            return;
        }

        setAddressStructureLoading(true);
        try {
            const { data } = await api.get("/addresses/structure");
            const countries = Array.isArray(data?.countries) ? data.countries : [];
            setAddressStructure({ countries });
        } catch {
            setAddressStructure({ countries: [] });
        } finally {
            setAddressStructureLoading(false);
        }
    }, [session?.token]);

    const refreshAddresses = useCallback(async () => {
        if (!session?.token) {
            setAddresses([]);
            return;
        }

        setAddressesLoading(true);
        try {
            const { data } = await api.get("/addresses");
            setAddresses(Array.isArray(data) ? data : []);
        } catch {
            setAddresses([]);
        } finally {
            setAddressesLoading(false);
        }
    }, [session?.token]);

    const createAddress = useCallback(async (payload) => {
        const { data } = await api.post("/addresses", payload);
        await refreshAddresses();
        return data;
    }, [refreshAddresses]);

    const updateAddress = useCallback(async (addressId, payload) => {
        const { data } = await api.put(`/addresses/${addressId}`, payload);
        await refreshAddresses();
        return data;
    }, [refreshAddresses]);

    const deleteAddress = useCallback(async (addressId) => {
        const { data } = await api.delete(`/addresses/${addressId}`);
        await refreshAddresses();
        return data;
    }, [refreshAddresses]);

    const sendOtp = useCallback(async (identifier) => {
        const { data } = await api.post("/auth/send-otp", { identifier });
        return data;
    }, []);

    const verifyOtp = useCallback(async ({ identifier, verificationCode }) => {
        const { data } = await api.post("/auth/verify-otp", { identifier, verificationCode });
        const token = data?.token || "";
        const user = data?.user || null;

        setSession({ token, user });
        return data;
    }, []);

    useEffect(() => {
        if (session?.token) {
            refreshAddressStructure();
            refreshAddresses();
        } else {
            setAddressStructure({ countries: [] });
            setAddresses([]);
        }
    }, [session?.token, refreshAddressStructure, refreshAddresses]);

    const setAuthSession = ({ token, user }) => {
        setSession({ token: token || "", user: user || null });
    };

    const clearAuthSession = () => {
        setSession({ token: "", user: null });
        setAddressStructure({ countries: [] });
        setAddresses([]);
    };

    const value = useMemo(() => ({
        token: session.token,
        user: session.user,
        isAuthenticated: Boolean(session.token),
        sendOtp,
        verifyOtp,
        setAuthSession,
        clearAuthSession,
        addressStructure,
        addressStructureLoading,
        addresses,
        addressesLoading,
        refreshAddresses,
        createAddress,
        updateAddress,
        deleteAddress,
        refreshAddressStructure,
    }), [
        session,
        sendOtp,
        verifyOtp,
        addressStructure,
        addressStructureLoading,
        addresses,
        addressesLoading,
        refreshAddresses,
        createAddress,
        updateAddress,
        deleteAddress,
        refreshAddressStructure,
    ]);

    return (
        <AuthSessionContext.Provider value={value}>
            {children}
        </AuthSessionContext.Provider>
    );
}

export function useAuthSession() {
    const ctx = useContext(AuthSessionContext);
    if (!ctx) throw new Error("useAuthSession must be used inside AuthSessionProvider");
    return ctx;
}
